import React, {useRef} from "react";

type TvMediaRowProps = {
    rowId: string;
    title: React.ReactNode;
    icon?: React.ReactNode;
    count?: number;
    order?: number;
    showReorderControls?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onHideRow?: () => void;
    extraHeaderActions?: React.ReactNode;
    onScrollEnd?: () => void;
    children: React.ReactNode;
};

const TvMediaRow: React.FC<TvMediaRowProps> = ({
    rowId,
    title,
    icon,
    count,
    order = 0,
    showReorderControls = false,
    isFirst = false,
    isLast = false,
    onMoveUp,
    onMoveDown,
    onHideRow,
    extraHeaderActions,
    onScrollEnd,
    children
}) => {
    const trackRef = useRef<HTMLDivElement>(null);

    const handleScroll = () => {
        if (!onScrollEnd || !trackRef.current) return;
        const {scrollLeft, scrollWidth, clientWidth} = trackRef.current;
        if (scrollLeft + clientWidth >= scrollWidth - 280) {
            onScrollEnd();
        }
    };

    const scrollByStep = (dir: -1 | 1) => {
        if (!trackRef.current) return;
        trackRef.current.scrollBy({
            left: dir * 680,
            behavior: "smooth"
        });
    };

    return (
        <section className="tv-media-row" style={{order}}>
            <div
                className="tv-media-row__header"
                data-tv-row={showReorderControls ? `${rowId}-header` : undefined}>
                <div className="tv-media-row__title-group">
                    {icon && <span className="tv-media-row__icon">{icon}</span>}
                    <h2 className="tv-media-row__title">{title}</h2>
                    {typeof count === "number" && count > 0 && (
                        <span className="tv-media-row__count">{count}</span>
                    )}
                </div>

                <div className="tv-media-row__actions">
                    {extraHeaderActions}
                    {showReorderControls && (
                        <>
                            <button
                                type="button"
                                data-tv-focusable="true"
                                disabled={isFirst}
                                className="tv-btn tv-btn--ghost tv-btn--sm"
                                onClick={onMoveUp}>
                                <i className="fa-solid fa-arrow-up pe-2"></i>Subir
                            </button>
                            <button
                                type="button"
                                data-tv-focusable="true"
                                disabled={isLast}
                                className="tv-btn tv-btn--ghost tv-btn--sm"
                                onClick={onMoveDown}>
                                <i className="fa-solid fa-arrow-down pe-2"></i>Bajar
                            </button>
                            {onHideRow && (
                                <button
                                    type="button"
                                    data-tv-focusable="true"
                                    className="tv-btn tv-btn--ghost tv-btn--sm"
                                    onClick={onHideRow}
                                    title="Ocultar fila">
                                    <i className="fa-solid fa-eye-slash pe-2"></i>Ocultar
                                </button>
                            )}
                        </>
                    )}
                    <button
                        type="button"
                        tabIndex={-1}
                        className="tv-media-row__scroll-arrow"
                        onClick={() => scrollByStep(-1)}
                        aria-hidden="true">
                        <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button
                        type="button"
                        tabIndex={-1}
                        className="tv-media-row__scroll-arrow"
                        onClick={() => scrollByStep(1)}
                        aria-hidden="true">
                        <i className="fa-solid fa-chevron-right"></i>
                    </button>
                </div>
            </div>

            <div
                ref={trackRef}
                data-tv-row={rowId}
                onScroll={handleScroll}
                className="tv-media-row__track">
                {children}
            </div>
        </section>
    );
};

export default TvMediaRow;
